import type { FC, KeyboardEvent, MouseEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { formatTs } from '@/helpers'
import { rqKeys } from '@/react-query-keys'
import {
  DASHBOARD_MESSAGES_SHOW_LIMIT,
  findVisitorMessageById,
  markVisitorMessagesAsRead,
  useMessagesStore,
} from '@/pages/messages'
import {
  SC_DashboardMessagesBlock,
  SC_DashboardMessageCard,
  SC_DashboardMessageClose,
  SC_DashboardMessageContent,
  SC_DashboardMessageDate,
  SC_DashboardMessageHeader,
  SC_DashboardMessageSubject,
  SC_DashboardMessagesTitle,
  SC_DashboardMessagesTitleIcon,
  SC_DashboardMessagesTitleRow,
  SC_DashboardMessagesWrapper,
} from './styled'

export const DashboardMessages: FC = () => {
  const queryClient = useQueryClient()
  const dashboardMessages = useMessagesStore((s) => s.listDashboardMessages(DASHBOARD_MESSAGES_SHOW_LIMIT))
  const markMessagesAsRead = useMessagesStore((s) => s.markMessagesAsRead)
  const openMessenger = useMessagesStore((s) => s.openMessenger)

  if (!dashboardMessages.length) return null

  const markAsRead = (messageId: number) => {
    markMessagesAsRead([ messageId ])
    void markVisitorMessagesAsRead([ messageId ]).catch((error) => {
      console.warn('[dashboard-messages] failed to mark message as read', error)
      void queryClient.invalidateQueries({ queryKey: [ rqKeys.visitorMessageUnread ], exact: false })
    })
  }

  const handleCardClick = async (messageId: number, topicId: number) => {
    markAsRead(messageId)
    const normalizedTopicId = Number(topicId)

    if (Number.isFinite(normalizedTopicId) && normalizedTopicId > 0) {
      openMessenger({
        topicId: normalizedTopicId,
        messageId,
      })
      return
    }

    try {
      const found = await findVisitorMessageById(messageId)
      const resolvedTopicId = Number(found?.topic?.id)
      if (Number.isFinite(resolvedTopicId) && resolvedTopicId > 0) {
        openMessenger({
          topicId: resolvedTopicId,
          messageId,
        })
        return
      }
    } catch (error) {
      console.warn('[dashboard-messages] failed to resolve topic before open', error)
    }

    openMessenger({
      topicId: null,
      messageId,
    })
  }

  const handleCloseClick = (e: MouseEvent<HTMLButtonElement>, messageId: number) => {
    e.preventDefault()
    e.stopPropagation()
    markAsRead(messageId)
  }

  const handleCardKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    messageId: number,
    topicId: number
  ) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    void handleCardClick(messageId, topicId)
  }

  return (
    <SC_DashboardMessagesWrapper>
      <SC_DashboardMessagesBlock>
        <SC_DashboardMessagesTitleRow>
          <SC_DashboardMessagesTitleIcon>
            <svg viewBox='0 0 24 24' aria-hidden='true'>
              <path d='M10 20h4V4h-4v16zM4 20h4V10H4v10zM16 20h4v-7h-4v7z' />
            </svg>
          </SC_DashboardMessagesTitleIcon>
          <SC_DashboardMessagesTitle>Сообщения</SC_DashboardMessagesTitle>
        </SC_DashboardMessagesTitleRow>

        {dashboardMessages.map((message) => (
          <SC_DashboardMessageCard
            key={message.id}
            role='button'
            tabIndex={0}
            onClick={() => {
              void handleCardClick(message.id, message.topic.id)
            }}
            onKeyDown={(e) => handleCardKeyDown(e, message.id, message.topic.id)}
          >
            <SC_DashboardMessageHeader>
              <div style={{ minWidth: 0 }}>
                <SC_DashboardMessageSubject>
                  {message.topic.subject || 'Сообщение'}
                </SC_DashboardMessageSubject>
                <SC_DashboardMessageDate>
                  {formatTs(message.timestamp.getTime())}
                </SC_DashboardMessageDate>
              </div>

              <SC_DashboardMessageClose
                type='button'
                onClick={(e) => handleCloseClick(e, message.id)}
                title='Скрыть'
              >
                ×
              </SC_DashboardMessageClose>
            </SC_DashboardMessageHeader>

            <SC_DashboardMessageContent>
              {message.content || 'Без текста сообщения'}
            </SC_DashboardMessageContent>
          </SC_DashboardMessageCard>
        ))}
      </SC_DashboardMessagesBlock>
    </SC_DashboardMessagesWrapper>
  )
}
